import React from "react";
import { 
  Stack, 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Card, 
  CardContent,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  IconButton
} from "@mui/material";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import CloseIcon from '@mui/icons-material/Close';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const carouselImages = [
  "/images/food-bank/banco-alimentos-1.jpeg",
  "/images/food-bank/banco-alimentos-2.jpeg",
  "/images/food-bank/banco-alimentos-3.jpeg",
  "/images/food-bank/banco-alimentos-4.jpeg",
  "/images/food-bank/banco-alimentos-5.jpeg",
  "/images/food-bank/banco-alimentos-6.jpeg",
  "/images/food-bank/banco-alimentos-7.jpeg",
  "/images/food-bank/banco-alimentos-8.jpeg",
  "/images/food-bank/banco-alimentos-9.jpeg",
  "/images/food-bank/banco-alimentos-10.jpeg",
  "/images/food-bank/banco-alimentos-11.jpeg",
  "/images/food-bank/banco-alimentos-12.jpeg",
  "/images/food-bank/banco-alimentos-13.jpeg",
  "/images/food-bank/banco-alimentos-14.jpeg",
  "/images/food-bank/banco-alimentos-15.jpeg",
  "/images/food-bank/banco-alimentos-16.jpeg",
  "/images/food-bank/banco-alimentos-17.jpeg"
];

export default function FoodBankPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '' });
  const [previewImage, setPreviewImage] = React.useState(null);
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);

  const handleCopy = (text, message) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: '' });
  };

  const handleOpenPreview = (image, index) => {
    setPreviewImage(image);
    setCurrentImageIndex(index);
  };

  const handleClosePreview = () => {
    setPreviewImage(null);
  };

  const handleNextImage = () => {
    const nextIndex = (currentImageIndex + 1) % carouselImages.length;
    setCurrentImageIndex(nextIndex);
    setPreviewImage(carouselImages[nextIndex]);
  };

  const handlePrevImage = () => {
    const prevIndex = (currentImageIndex - 1 + carouselImages.length) % carouselImages.length;
    setCurrentImageIndex(prevIndex);
    setPreviewImage(carouselImages[prevIndex]);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={5}>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mb: 2
            }}
          >
            <img 
              src="/images/food-bank/logo.png" 
              alt="Logo Banco de Alimentos"
              style={{ 
                maxWidth: '300px', 
                height: 'auto',
                width: '100%'
              }}
            />
          </Box>
          
          <Typography 
            variant="h5" 
            component="h2" 
            sx={{ 
              fontWeight: 600, 
              color: 'secondary.main',
              fontSize: { xs: '1.5rem', md: '1.75rem' }
            }}
          >
            Alimente a solidariedade!
          </Typography>
        </Box>

        <Card 
          elevation={0} 
          sx={{ 
            backgroundColor: '#f5f5f5',
            borderRadius: 4,
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                fontSize: { xs: '1rem', md: '1.125rem' },
                fontWeight: 500
              }}
            >
              <strong>Você sabia que milhares de famílias em nossa cidade enfrentam a fome?</strong>
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                fontSize: { xs: '1rem', md: '1.125rem' }
              }}
            >
              O Banco de Alimentos é uma solução para reduzir o desperdício e garantir que alimentos cheguem às pessoas que mais precisam.
            </Typography>

            <Typography 
              variant="body1" 
              sx={{ 
                mb: 2,
                fontSize: { xs: '1rem', md: '1.125rem' }
              }}
            >
              É um equipamento da rede de segurança alimentar que tem como principal objetivo a captação, recepção e distribuição gratuita de gêneros alimentícios provenientes de doações, tanto do setor público quanto do privado. Com foco na redução do desperdício, aproveitando integralmente os alimentos disponíveis, faz com que alimentos que ainda estão em boas condições cheguem até aqueles que mais precisam, funcionando como um elo entre aqueles que podem contribuir e as populações que mais necessitam.
            </Typography>

            <Typography 
              variant="body1" 
              sx={{ 
                fontSize: { xs: '1rem', md: '1.125rem' }
              }}
            >
              Seja por meio de doações individuais ou de grandes empresas, o Banco de Alimentos faz a diferença, promovendo um ciclo de solidariedade e apoio àqueles que enfrentam dificuldades, ao mesmo tempo que trabalha para preservar recursos e garantir o acesso a uma alimentação digna.
            </Typography>
          </CardContent>
        </Card>

        <Box>
          <Typography 
            variant="h5" 
            component="h3" 
            sx={{ 
              mb: 3, 
              fontWeight: 600,
              textAlign: 'center',
              color: 'secondary.main'
            }}
          >
            Conheça nosso trabalho
          </Typography>

          <Box sx={{ 
            '& .swiper': { 
              borderRadius: 2,
              overflow: 'hidden',
              width: '100%',
              height: isMobile ? '250px' : isTablet ? '350px' : '450px',
              mb: 2
            },
            '& .swiper-pagination': {
              bottom: '10px',
              '& .swiper-pagination-bullet': {
                width: '10px',
                height: '10px',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                opacity: 0.5,
                '&.swiper-pagination-bullet-active': {
                  opacity: 1,
                  backgroundColor: 'white'
                }
              }
            },
            '& .swiper-button-next, & .swiper-button-prev': {
              color: 'white',
              background: 'rgba(0, 0, 0, 0.3)',
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              '&::after': {
                fontSize: '18px',
                fontWeight: 'bold'
              },
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.5)'
              }
            },
            '& .swiper-button-next': {
              right: '10px'
            },
            '& .swiper-button-prev': {
              left: '10px'
            }
          }}>
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={0}
              slidesPerView={1}
              navigation
              pagination={{ clickable: true }}
              autoplay={{ delay: 5000 }}
            >
              {carouselImages.map((image, index) => (
                <SwiperSlide key={index}>
                  <Box
                    onClick={() => handleOpenPreview(image, index)}
                    sx={{
                      backgroundImage: `url(${image})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      cursor: 'pointer',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        zIndex: 1
                      }
                    }}
                  >
                  </Box>
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
        </Box>

        <Card 
          sx={{ 
            borderRadius: 4,
            backgroundColor: theme.palette.secondary.main,
            color: 'white',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography 
              variant="h5" 
              component="h3" 
              sx={{ 
                mb: 2, 
                fontWeight: 700,
                textAlign: 'center',
                color: 'white'
              }}
            >
              Como doar?
            </Typography>

            <Typography 
              variant="body1" 
              sx={{ 
                fontSize: { xs: '1rem', md: '1.125rem' },
                textAlign: 'center',
                color: 'white'
              }}
            >
              Você pode doar alimentos não perecíveis e perecíveis em nosso banco de alimentos. A sua doação se transforma em refeições nutritivas e saudáveis, alimentando milhares de famílias em situação de insegurança alimentar e nutricional.
            </Typography>
          </CardContent>
        </Card>

        <Box sx={{ mt: 4 }}>
          <Typography 
            variant="h5" 
            component="h3" 
            sx={{ 
              mb: 3, 
              fontWeight: 600,
              textAlign: 'center',
              color: 'secondary.main'
            }}
          >
            Informações de Contato
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  height: '100%',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
                    cursor: 'pointer'
                  }
                }}
                onClick={() => handleCopy('Av. Norte Miguel Arraes de Alencar, nº1716, Torreão, Recife-PE', 'Endereço copiado!')}
              >
                <CardContent sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  p: 3
                }}>
                  <LocationOnIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
                    Endereço
                  </Typography>
                  <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                    Av. Norte Miguel Arraes de Alencar, nº1716, Torreão, Recife-PE
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  height: '100%',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
                    cursor: 'pointer'
                  }
                }}
                onClick={() => handleCopy('(81) 33033-3173', 'Telefone copiado!')}
              >
                <CardContent sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  p: 3
                }}>
                  <PhoneIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
                    Telefone
                  </Typography>
                  <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                    (81) 3303-3317
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  height: '100%', 
                  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
                    cursor: 'pointer'
                  }
                }}
                onClick={() => handleCopy('bancodealimentos@recife.pe.gov.br', 'E-mail copiado!')}
              >
                <CardContent sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  p: 3
                }}>
                  <EmailIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
                    E-mail
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      textAlign: 'center', 
                      color: 'text.secondary',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      hyphens: 'auto'
                    }}
                  >
                    bancodealimentos@recife.pe.gov.br
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Box 
          sx={{ 
            textAlign: 'center', 
            p: 3, 
            bgcolor: 'primary.light', 
            borderRadius: 4,
            color: 'white'
          }}
        >
          <Box 
            sx={{ 
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 3, md: 5 },
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                Junte-se a nós nesta causa!
              </Typography>
              <Typography variant="body1">
                Sua contribuição faz toda a diferença para famílias em situação de vulnerabilidade.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Dialog
          open={!!previewImage}
          onClose={handleClosePreview}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'transparent',
              boxShadow: 'none',
              overflow: 'hidden'
            }
          }}
        >
          <DialogContent sx={{ p: 0, position: 'relative' }}>
            <IconButton
              onClick={handleClosePreview}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'white',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.7)'
                },
                zIndex: 2
              }}
            >
              <CloseIcon />
            </IconButton>
            <IconButton
              onClick={handlePrevImage}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.7)'
                },
                zIndex: 2
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <IconButton
              onClick={handleNextImage}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.7)'
                },
                zIndex: 2
              }}
            >
              <ArrowForwardIcon />
            </IconButton>
            <img
              src={previewImage}
              alt="Preview"
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '90vh',
                objectFit: 'contain'
              }}
            />
          </DialogContent>
        </Dialog>
      </Stack>
    </Container>
  );
}